/**
 * W5 (field test 2026-10-16) — WHEN does a technician's Dayboard column show the multi-bay workspace?
 *
 * Founder directive (absolute): a technician with an ordinary single-bay workflow sees the existing Dayboard
 * presentation UNCHANGED. The multi-bay sections appear ONLY when actual concurrent activity justifies them.
 *
 * Pure function of canonical data the column already holds, plus the server's open unattended operations:
 *   - a vehicle is LIVE IN A BAY for this technician when the ticket is in the column, work actually started there,
 *     it physically sits in a bay (a planned / dependent bay never counts — TKT-0277), and it is either
 *       · hands-on (timer running), or
 *       · running an unattended operation (a server row says so — never inferred), or
 *       · switched away (paused by a vehicle switch — the vehicle waits in its bay for the same technician), or
 *       · paused with a bay-blocking hold (the vehicle still occupies the bay);
 *   - ACTIVE when, on the shop-local TODAY only, live vehicles occupy at least TWO distinct bays AND at least one of
 *     them is hands-on or unattended (two cars both paused for parts over lunch do not switch the layout — W5-UI-1).
 *
 * Never inputs: `bay_assignments` (assigned bays are not activity), planned bay ids, other days, other columns.
 * No hysteresis: the board is current; the answer is recomputed every render.
 */
import { isBayBlockingHoldReason } from '@/utils/bayHoldOwner';
import { legacyPhysicalBayId } from '@/lib/location/model';
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import type { UnattendedFlagKind } from '@/lib/timer/unattendedFlags';

export type LiveBayState = 'hands_on' | 'unattended' | 'switched_away' | 'paused_in_bay';

/** The pause reason switch_my_work records (catalog detail switched_vehicle). The ticket row carries only the
 *  reason projection; the server read model (technician_workspace_day) classifies by pause_intent. */
export const SWITCHED_PAUSE_REASON = 'Switched to another vehicle';

export interface OpenUnattendedOperation {
  id: string;
  ticket_id: string;
  kind: string;
  kind_note?: string | null;
  started_at: string;
  expected_minutes?: number | null;
  started_by_technician_id?: string | null;
  bay_id_at_start?: string | null;
  /** B-T1: the server's unresolved flags on this operation (display only; absent = none). */
  flags?: UnattendedFlagKind[];
}

export interface LiveBayVehicle {
  ticketId: string;
  bayId: string;
  state: LiveBayState;
  /** Pause reason when paused (the technician's answer, shown verbatim). */
  holdReason: string | null;
  /** Open machine processes on this vehicle (server rows). */
  unattended: readonly OpenUnattendedOperation[];
}

export interface MultiBayActivity {
  active: boolean;
  vehicles: LiveBayVehicle[];
  distinctBays: number;
}

export const INACTIVE_MULTI_BAY: MultiBayActivity = Object.freeze({
  active: false,
  vehicles: [],
  distinctBays: 0,
}) as MultiBayActivity;

/** The minimal ticket shape the predicate reads (a Dayboard ticket row). */
export interface MultiBayTicketLike {
  id: string;
  status?: string | null;
  timer_status?: string | null;
  actually_started?: boolean | null;
  bay_id?: string | null;
  parking_lot?: boolean | null;
  parent_ticket_id?: string | null;
  rollover_of_ticket_id?: string | null;
  pause_reason?: string | null;
}

export interface DeriveMultiBayInput {
  /** EXACTLY the column's tickets (getFilteredTickets output). */
  techTickets: readonly MultiBayTicketLike[];
  /** selectedDayLabel === shop-local today. */
  isShopToday: boolean;
  /** Open unattended operations keyed by ticket id (W5 server rows). Undefined/empty = none. */
  unattendedByTicketId?: ReadonlyMap<string, readonly OpenUnattendedOperation[]> | null;
  /** Physical bay of a ticket; defaults to the legacy physical projection (never a planned bay). */
  physicalBayOf?: (t: MultiBayTicketLike) => string | null;
}

/** Kill switch: VITE_DAYBOARD_MULTIBAY=off restores the single layout everywhere. Default on. */
export function isMultiBayWorkspaceEnabled(): boolean {
  return import.meta.env.VITE_DAYBOARD_MULTIBAY !== 'off';
}

export function classifyLiveVehicle(
  t: MultiBayTicketLike,
  unattended: readonly OpenUnattendedOperation[],
  physicalBayOf: (t: MultiBayTicketLike) => string | null = legacyPhysicalBayId,
): LiveBayVehicle | null {
  if (t.actually_started !== true) return null;
  if (t.status === 'Completed' || t.status === 'Cancelled' || isTicketDeleted(t as never)) return null;
  const bayId = physicalBayOf(t);
  if (!bayId) return null;
  if (t.timer_status === 'running') {
    return { ticketId: t.id, bayId, state: 'hands_on', holdReason: null, unattended };
  }
  if (unattended.length > 0) {
    return { ticketId: t.id, bayId, state: 'unattended', holdReason: t.pause_reason ?? null, unattended };
  }
  // Paused work: the timer projection (timer_status) is the live truth; status 'Paused' is the legacy shape.
  const paused = t.timer_status === 'paused' || t.status === 'Paused';
  if (paused && (t.pause_reason ?? '').trim().toLowerCase() === SWITCHED_PAUSE_REASON.toLowerCase()) {
    // Switched away: the vehicle still holds its bay, waiting for this technician to come back.
    return { ticketId: t.id, bayId, state: 'switched_away', holdReason: t.pause_reason ?? null, unattended };
  }
  if (paused && isBayBlockingHoldReason(t.pause_reason ?? null)) {
    return { ticketId: t.id, bayId, state: 'paused_in_bay', holdReason: t.pause_reason ?? null, unattended };
  }
  return null;
}

export function deriveMultiBayActivity(input: DeriveMultiBayInput): MultiBayActivity {
  if (!input.isShopToday) return INACTIVE_MULTI_BAY;
  const map = input.unattendedByTicketId;
  const vehicles: LiveBayVehicle[] = [];
  for (const t of input.techTickets) {
    const ops = map?.get(t.id) ?? [];
    const v = classifyLiveVehicle(t, ops, input.physicalBayOf);
    if (v) vehicles.push(v);
  }
  const distinctBays = new Set(vehicles.map((v) => v.bayId)).size;
  const engaged = vehicles.some((v) => v.state === 'hands_on' || v.state === 'unattended');
  if (distinctBays < 2 || !engaged) return INACTIVE_MULTI_BAY;
  return { active: true, vehicles, distinctBays };
}

/**
 * The sections, in display order. Each ticket of the column appears in EXACTLY one section and keeps its original
 * column index (drag-and-drop and the ghost preview are index-based).
 */
export interface MultiBaySections<T> {
  handsOn: Array<{ ticket: T; idx: number; vehicle: LiveBayVehicle }>;
  unattended: Array<{ ticket: T; idx: number; vehicle: LiveBayVehicle }>;
  pausedInBay: Array<{ ticket: T; idx: number; vehicle: LiveBayVehicle }>;
  queue: Array<{ ticket: T; idx: number }>;
}

export function partitionMultiBaySections<T extends { id: string }>(
  techTickets: readonly T[],
  activity: MultiBayActivity,
): MultiBaySections<T> {
  const byId = new Map(activity.vehicles.map((v) => [v.ticketId, v]));
  const out: MultiBaySections<T> = { handsOn: [], unattended: [], pausedInBay: [], queue: [] };
  techTickets.forEach((ticket, idx) => {
    const v = byId.get(ticket.id);
    if (!v) out.queue.push({ ticket, idx });
    else if (v.state === 'hands_on') out.handsOn.push({ ticket, idx, vehicle: v });
    else if (v.state === 'unattended') out.unattended.push({ ticket, idx, vehicle: v });
    else out.pausedInBay.push({ ticket, idx, vehicle: v });
  });
  return out;
}

/** Expected end of an unattended operation, or null when the person stated none (unknown stays unknown). */
export function unattendedExpectedEnd(op: Pick<OpenUnattendedOperation, 'started_at' | 'expected_minutes'>): Date | null {
  if (op.expected_minutes == null) return null;
  const start = new Date(op.started_at).getTime();
  if (!Number.isFinite(start)) return null;
  return new Date(start + op.expected_minutes * 60_000);
}
